import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import Tabs from '@material-ui/core/Tabs';
import MuiTab from '@material-ui/core/Tab';

import {
    getConfig,
    getGames,
    newGame as getNewGame,
    getGame,
} from '../../services/Master';
import { convertGameData } from '../../services/shared';
import {
    useAuthContext,
    changeLanguage,
    setHelpUrl,
} from '../../contexts/AuthContext';
import Highscores from '../Highscores';
import Results from './Results';
import GamesPanel from './GamesPanel';
import GameSettings from './GameSettings';
import UserList from './UserList';
import { headerHeight } from '../AppHeader';
import ErrorSnackbarMessage from '../Dialog/ErrorSnackbarMessage';

const MainContainer = styled.div`
    display: flex;
    align-items: stretch;
    flex-grow: 1;
    height: calc(100% - ${headerHeight}px);
    /* min-height: 100vh; */
`;
const StyledGamesPanel = styled(GamesPanel)`
    width: 300px;
    flex-basis: 300px;
    flex-shrink: 0;
    border-right: 2px solid black;
`;
const GameContainer = styled.div`
    flex-grow: 1;
    height: 100%;
    overflow: auto;
`;

const tabsHeaderHeight = 48;
const TabsHeader = styled(Tabs)`
    background: #fff;
    padding-left: 8px;
`;

const TabContainer = styled.div`
    height: calc(100% - ${tabsHeaderHeight}px);
    overflow-y: auto;
`;
const TabContentContainer = styled.div`
    padding: 16px;
    box-sizing: border-box;
`;
const StyledGameSettings = styled(GameSettings)`
    /* flex-grow: 1; */
`;
const Tab = styled(MuiTab)`
    /* min-width: 40px; */
`;

const TabContent = ({
    tabId,
    gameData,
    parameterConfig,
    onSaveGame,
    onDeleteGame,
    gameToken,
}) => {
    if (tabId === 0) {
        return (
            <StyledGameSettings
                gameData={gameData}
                parameterConfig={parameterConfig}
                onSave={onSaveGame}
                onDelete={onDeleteGame}
            />
        );
    }
    if (tabId === 1) {
        return (
            <TabContentContainer>
                <UserList gameToken={gameToken} />
            </TabContentContainer>
        );
    }
    if (tabId === 2) {
        return (
            <TabContentContainer>
                <Highscores gameToken={gameToken} />
            </TabContentContainer>
        );
    }
    if (tabId === 3) {
        return (
            <TabContentContainer style={{ height: '100%' }}>
                <Results gameToken={gameToken} />
            </TabContentContainer>
        );
    }
    return null;
};

const Master = () => {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const [parameterConfig, setParameterConfig] = useState({});
    const { auth, dispatch } = useAuthContext();
    const [gameList, setGameList] = useState([]);
    const [gameData, setGameData] = useState();
    // const [isLoading, setIsLoading] = useState(false);
    const [activeTab, setActiveTab] = useState(0);

    const gameToken = gameData?.['@token'];

    const refreshGames = () => {
        getGames(auth.token)
            .then((data) => {
                const list = Object.entries(data).map(([key, value]) => {
                    return {
                        key,
                        ...value,
                    };
                });
                setGameList(list);
            })
            .catch((e) => {
                console.warn(e);
            });
    };

    useEffect(() => {
        if (auth.ready && auth.token) {
            getGames(auth.token)
                .then((data) => {
                    setGameList(
                        Object.entries(data).map(([key, value]) => {
                            return {
                                key,
                                ...value,
                            };
                        })
                    );
                })
                .catch((e) => {
                    enqueueSnackbar(
                        <ErrorSnackbarMessage
                            tKey="error.loadGames"
                            error={e}
                        />,
                        {
                            variant: 'error',
                        }
                    );
                    console.warn(e);
                });
        }
    }, [auth.ready, auth.token]);

    const handleSelectGame = (game) => {
        // @todo: set ismounted as state
        const ismounted = true;
        // setIsLoading(true);
        getGame(auth.token, game, parameterConfig)
            .then((data) => {
                if (ismounted) {
                    const configKey = data['@config'];
                    const configData = auth.configs.find(
                        (c) => c._configKey === configKey
                    );
                    if (!configData) {
                        throw new Error('Cannot find config file ' + configKey);
                    }
                    setParameterConfig(configData);
                    setGameData(convertGameData(data, configData));
                    dispatch(changeLanguage(data?.language));

                    const helpSettings = configData?.['@help'];
                    if (
                        helpSettings &&
                        helpSettings.wikiurl &&
                        helpSettings.master?.default
                    ) {
                        dispatch(
                            setHelpUrl(
                                `${helpSettings.wikiurl}${helpSettings.master.default}`
                            )
                        );
                    } else {
                        setHelpUrl(null);
                    }
                }
            })
            .catch((e) => {
                enqueueSnackbar(
                    <ErrorSnackbarMessage
                        tKey="error.loadGame"
                        tOptions={{ game }}
                        error={e}
                    />,
                    {
                        variant: 'error',
                    }
                );
                if (ismounted) {
                    setGameData({ name: game, configError: true });
                }
            });
        // .finally(() => {
        //     if (ismounted) {
        //         setIsLoading(false);
        //     }
        // });
    };
    const onSaveGame = (savedGameName) => {
        refreshGames();
        handleSelectGame(savedGameName);
    };

    const handleAddGame = (configKey) => {
        // setIsLoading(true);
        const configData = auth.configs.find((c) => c._configKey === configKey);
        if (!configData) {
            throw new Error('Unknown configuration');
        }
        setParameterConfig(configData);

        const helpSettings = configData?.['@help'];
        if (
            helpSettings &&
            helpSettings.wikiurl &&
            helpSettings.master?.default
        ) {
            dispatch(
                setHelpUrl(
                    `${helpSettings.wikiurl}${helpSettings.master.default}`
                )
            );
        } else {
            setHelpUrl(null);
        }

        setGameData(null);
        setActiveTab(0);
        getNewGame(auth.token, configData)
            .then((game) => {
                setGameData(game);
            })
            .catch((e) => {
                console.warn(e);
            });
    };

    const onDeleteGame = () => {
        setGameData(null);
        refreshGames();
    };

    const handleChangeTab = (event, newTab) => {
        setActiveTab(newTab);
    };

    return (
        <MainContainer>
            <StyledGamesPanel
                games={gameList}
                selectedGame={gameData?.name}
                setSelectedGame={parameterConfig ? handleSelectGame : null}
                configs={auth?.configs?.map((config) => ({
                    label: config._configKey,
                    value: config._configKey,
                }))}
                addGame={parameterConfig ? handleAddGame : null}
            />
            <GameContainer>
                {gameData ? (
                    <>
                        <TabsHeader
                            value={activeTab}
                            onChange={handleChangeTab}
                            indicatorColor="primary"
                            textColor="primary"
                            // centered
                            variant="scrollable"
                        >
                            <Tab label={t('gamesettings.config')} />
                            <Tab
                                label={t('gamesettings.users')}
                                disabled={!gameToken}
                            />
                            <Tab
                                label={t('gamesettings.highscores')}
                                disabled={!gameToken}
                            />
                            <Tab
                                label={t('gamesettings.results')}
                                disabled={!gameToken}
                            />
                        </TabsHeader>
                        <TabContainer>
                            <TabContent
                                tabId={activeTab}
                                {...{
                                    gameData,
                                    parameterConfig,
                                    onSaveGame,
                                    onDeleteGame,
                                    gameToken,
                                }}
                            />
                        </TabContainer>
                    </>
                ) : (
                    <TabContentContainer>
                        {t('gamesettings.Select a game')}
                    </TabContentContainer>
                )}
            </GameContainer>
        </MainContainer>
    );
};
export default Master;
